import { Pipe, PipeTransform, inject } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import dayjs from "../../../core-ui/utils/dayjs";

type DateInput = dayjs.ConfigType;

function build(value: DateInput, locale?: string): dayjs.Dayjs | null {
  if (value === undefined || value === null || value === "") return null;
  const d = dayjs(value);
  if (!d.isValid()) return null;
  return locale ? d.locale(locale) : d;
}

@Pipe({
  name: "dayjsFormat",
  standalone: false,
})
export class DayjsFormatPipe implements PipeTransform {
  transform(value: DateInput, format: string, locale?: string): string {
    const d = build(value, locale);
    return d ? d.format(format) : "";
  }
}

@Pipe({
  name: "dayjsTimeAgo",
  standalone: false,
})
export class DayjsTimeAgoPipe implements PipeTransform {
  transform(value: DateInput, locale?: string): string {
    const d = build(value, locale);
    return d ? d.fromNow() : "";
  }
}

/**
 * dayjs ships no `calendar` strings with its locales, so `calendar()` always
 * falls back to the plugin's English defaults ("Today at ..."). We take the
 * formats from the translation files instead.
 */
const CALENDAR_KEYS = {
  lastDay: "calendarLastDay",
  sameDay: "calendarSameDay",
  nextDay: "calendarNextDay",
  nextWeek: "calendarNextWeek",
  lastWeek: "calendarLastWeek",
  sameElse: "calendarSameElse",
} as const;

@Pipe({
  name: "dayjsCalendar",
  standalone: false,
  // translations load asynchronously - recompute so the first render is not
  // stuck with the untranslated fallback
  pure: false,
})
export class DayjsCalendarPipe implements PipeTransform {
  private translate = inject(TranslateService);

  private calendarFormats(): Record<string, string> {
    const formats: Record<string, string> = {};
    for (const [slot, key] of Object.entries(CALENDAR_KEYS)) {
      const translated = this.translate.instant(key);
      // ngx-translate echoes the key back when nothing is found
      if (translated && translated !== key) {
        formats[slot] = translated;
      }
    }
    return formats;
  }

  transform(value: DateInput, locale?: string): string {
    const d = build(value, locale);
    return d ? d.calendar(null, this.calendarFormats()) : "";
  }
}
